import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Loading, Modal, Select, Pagination } from '../components/common';
import cardTransferService from '../services/cardTransferService';
import currencyService from '../services/currencyService';
import cashDrawerService from '../services/cashDrawerService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
  ArrowPathIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const CardTransfersPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [drawers, setDrawers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [transferType, setTransferType] = useState('incoming'); // incoming = receive, outgoing = send
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedDrawer, setSelectedDrawer] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState({
    amount: '',
    commission: '',
    notes: '',
    feeIncluded: true // Default to fee included (deducted from amount)
  });

  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceFormData, setBalanceFormData] = useState({
    balance: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [accountsRes, transfersRes, currenciesRes, drawersRes] = await Promise.all([
        cardTransferService.getAccounts({ active: true }),
        cardTransferService.getTransfers({ page, limit }),
        currencyService.getCurrencies({ active: true }),
        cashDrawerService.getDrawers({ active: true })
      ]);

      if (accountsRes.success) {
        setAccounts(accountsRes.data || []);
      }

      if (transfersRes.success) {
        // Handle paginated response
        if (transfersRes.data && Array.isArray(transfersRes.data.data)) {
          setTransfers(transfersRes.data.data);
          if (transfersRes.data.pagination) {
            setTotalPages(transfersRes.data.pagination.totalPages);
          }
        } else {
          setTransfers(transfersRes.data || []);
        }
      }

      const currencyData = currenciesRes.data || currenciesRes || [];
      const currencyList = Array.isArray(currencyData) ? currencyData : [];
      setCurrencies(currencyList);

      const drawerData = drawersRes.data || drawersRes || [];
      const drawerList = Array.isArray(drawerData) ? drawerData : [];
      setDrawers(drawerList);

      // Default to IQD currency
      if (currencyList.length > 0 && !selectedCurrency) {
        const iqd = currencyList.find(c => c.code === 'IQD');
        if (iqd) setSelectedCurrency(iqd.id);
        else setSelectedCurrency(currencyList[0].id);
      }

      // Default drawer
      if (drawerList.length > 0 && !selectedDrawer) {
        setSelectedDrawer(drawerList[0].id);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t, page, limit]); // Added limit dependency

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const openModal = (account, type) => {
    setSelectedAccount(account);
    setTransferType(type);
    setFormData({ amount: '', commission: '', notes: '', feeIncluded: true });
    setShowModal(true);
  };

  const openBalanceModal = (account) => {
    setSelectedAccount(account);
    setBalanceFormData({ balance: account.balance.toString(), notes: '' });
    setShowBalanceModal(true);
  };

  const handleBalanceSubmit = async (e) => {
    e.preventDefault();
    if (balanceFormData.balance === '' || parseFloat(balanceFormData.balance) < 0) {
      toast.error(t('validation.positiveNumber') || 'Balance must be a positive number');
      return;
    }

    try {
      setSubmitting(true);
      await cardTransferService.setAccountBalance(selectedAccount.uuid, {
        balance: parseFloat(balanceFormData.balance),
        notes: balanceFormData.notes.trim() || null
      });
      toast.success(t('cardTransfers.balanceUpdated'));
      setShowBalanceModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('validation.positiveNumber'));
      return;
    }

    if (!selectedDrawer) {
      toast.error(t('validation.selectDrawer', 'Please select a cash drawer'));
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        accountUuid: selectedAccount.uuid,
        customerName: transferType === 'incoming' ? 'Received' : 'Sent', // Internal marker
        amount: parseFloat(formData.amount),
        fee: parseFloat(formData.commission || 0),
        notes: formData.notes.trim() || null,
        currencyId: selectedCurrency,
        drawerId: selectedDrawer,
        feeIncluded: formData.feeIncluded
      };

      if (transferType === 'outgoing') {
        await cardTransferService.createOutgoingTransfer(payload);
        toast.success(t('cardTransfers.sendSuccess'));
      } else {
        await cardTransferService.createIncomingTransfer(payload);
        toast.success(t('cardTransfers.receiveSuccess'));
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransfer = async (uuid) => {
    if (!window.confirm(t('common.confirmDelete') || 'Are you sure you want to delete this transfer? This will revert all balance changes.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await cardTransferService.deleteTransfer(uuid);
      if (response.success) {
        toast.success(response.message || t('transactions.transactionDeleted'));
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, symbol = 'IQD') => {
    if (amount === null || amount === undefined) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' ' + symbol;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Calculate totals
  const calculateTotals = (accountUuid) => {
    const accountTransfers = transfers.filter(t => t.accountUuid === accountUuid && t.status === 'completed');
    const totalIn = accountTransfers
      .filter(t => t.direction === 'incoming')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const totalOut = accountTransfers
      .filter(t => t.direction === 'outgoing')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const totalCommission = accountTransfers
      .reduce((sum, t) => sum + parseFloat(t.fee || 0), 0);
    return { totalIn, totalOut, totalCommission };
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loading size="lg" /></div>;
  }

  const selectedCurrencyObj = currencies.find(c => c.id === parseInt(selectedCurrency));
  const currencySymbol = selectedCurrencyObj?.symbol || 'IQD';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            FIB / QI Card
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('cardTransfers.internalTracking')}
          </p>
        </div>
        <Button variant="secondary" onClick={fetchData}>
          <ArrowPathIcon className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Account Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {accounts.map((account) => {
          const { totalIn, totalOut, totalCommission } = calculateTotals(account.uuid);
          return (
            <Card key={account.uuid} className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 -mr-8 -mt-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full opacity-20"></div>

              <div className="relative">
                {/* Header */}
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <CreditCardIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-3 rtl:ml-0 rtl:mr-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{account.name}</h3>
                    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                      {account.provider}
                    </span>
                  </div>
                </div>

                {/* Balance */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('cardTransfers.currentBalance')}</p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                        {formatCurrency(account.balance)}
                      </p>
                    </div>
                    <button
                      onClick={() => openBalanceModal(account)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title={t('cardTransfers.editBalance')}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                    <p className="text-xs text-green-600 dark:text-green-400">{t('cardTransfers.totalIn')}</p>
                    <p className="text-sm font-bold text-green-700 dark:text-green-300">{formatCurrency(totalIn)}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
                    <p className="text-xs text-red-600 dark:text-red-400">{t('cardTransfers.totalOut')}</p>
                    <p className="text-sm font-bold text-red-700 dark:text-red-300">{formatCurrency(totalOut)}</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2">
                    <p className="text-xs text-purple-600 dark:text-purple-400">{t('cardTransfers.commission')}</p>
                    <p className="text-sm font-bold text-purple-700 dark:text-purple-300">{formatCurrency(totalCommission)}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => openModal(account, 'incoming')}
                  >
                    <ArrowDownIcon className="h-4 w-4 mr-1" />
                    {t('cardTransfers.receive')}
                  </Button>
                  <Button
                    className="w-full bg-red-600 hover:bg-red-700"
                    onClick={() => openModal(account, 'outgoing')}
                  >
                    <ArrowUpIcon className="h-4 w-4 mr-1" />
                    {t('cardTransfers.send')}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Recent Transfers */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('cardTransfers.recentTransfers')}
        </h2>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="table-header">{t('common.date')}</th>
                <th className="table-header">{t('cardTransfers.account')}</th>
                <th className="table-header">{t('cardTransfers.type')}</th>
                <th className="table-header text-right">{t('common.amount')}</th>
                <th className="table-header text-right">{t('cardTransfers.commission')}</th>
                <th className="table-header">{t('common.notes')}</th>
                {isAdmin() && <th className="table-header">{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {transfers.map((transfer) => (
                <tr key={transfer.uuid} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="table-cell text-sm">{formatDateTime(transfer.createdAt)}</td>
                  <td className="table-cell">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                      {transfer.provider}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className={`flex items-center gap-1 text-sm font-medium ${transfer.direction === 'incoming'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                      }`}>
                      {transfer.direction === 'incoming' ? (
                        <>
                          <ArrowDownIcon className="h-4 w-4" />
                          {t('cardTransfers.received')}
                        </>
                      ) : (
                        <>
                          <ArrowUpIcon className="h-4 w-4" />
                          {t('cardTransfers.sent')}
                        </>
                      )}
                    </span>
                  </td>
                  <td className={`table-cell text-right font-bold ${transfer.direction === 'incoming' ? 'text-green-600' : 'text-red-600'
                    }`}>
                    {transfer.direction === 'incoming' ? '+' : '-'}{formatCurrency(transfer.amount)}
                  </td>
                  <td className="table-cell text-right text-purple-600 dark:text-purple-400">
                    {parseFloat(transfer.fee || 0) > 0 ? `+${formatCurrency(transfer.fee)}` : '-'}
                  </td>
                  <td className="table-cell text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                    {transfer.notes || '-'}
                  </td>
                  {isAdmin() && (
                    <td className="table-cell">
                      <button
                        onClick={() => handleDeleteTransfer(transfer.uuid)}
                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {transfers.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('common.rowsPerPage')}:</span>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </select>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </Card>

      {/* Transfer Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          <div className="flex items-center gap-2">
            {transferType === 'incoming' ? (
              <ArrowDownIcon className="h-5 w-5 text-green-500" />
            ) : (
              <ArrowUpIcon className="h-5 w-5 text-red-500" />
            )}
            {transferType === 'incoming'
              ? t('cardTransfers.receiveFrom')
              : t('cardTransfers.sendTo')
            } - {selectedAccount?.provider}
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Balance */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">{t('cardTransfers.currentBalance')}:</span>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(selectedAccount?.balance)}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className={`p-3 rounded-lg text-sm ${transferType === 'incoming'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
            {transferType === 'incoming'
              ? t('cardTransfers.receiveDescription')
              : t('cardTransfers.sendDescription')
            }
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Currency Selection */}
            <Select
              label={t('common.currency')}
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              options={currencies.map(c => ({ value: c.id, label: `${c.code} - ${c.name}` }))}
            />

            {/* Cash Drawer Selection */}
            <Select
              label={t('common.cashDrawer')}
              value={selectedDrawer}
              onChange={(e) => setSelectedDrawer(e.target.value)}
              options={drawers.map(d => ({ value: d.id, label: d.name }))}
              placeholder={t('common.selectDrawer')}
            />
          </div>

          {/* Amount */}
          <Input
            label={t('common.amount') + ' *'}
            type="number"
            min="0"
            step="any"
            value={formData.amount}
            onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
            placeholder="0.00"
          />

          {/* Commission */}
          <Input
            label={t('cardTransfers.commission')}
            type="number"
            min="0"
            step="any"
            value={formData.commission}
            onChange={(e) => setFormData(prev => ({ ...prev, commission: e.target.value }))}
            placeholder="0.00"
          />

          {/* Fee Included Toggle */}
          {parseFloat(formData.commission || 0) > 0 && (
            <div className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <input
                type="checkbox"
                id="feeIncluded"
                checked={formData.feeIncluded}
                onChange={(e) => setFormData(prev => ({ ...prev, feeIncluded: e.target.checked }))}
                className="w-5 h-5 text-yellow-600 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500 dark:focus:ring-yellow-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label htmlFor="feeIncluded" className="flex-1 cursor-pointer">
                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  {t('cardTransfers.feeIncluded', 'Fee Included (Deduct from amount)')}
                </span>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
                  {formData.feeIncluded
                    ? (transferType === 'incoming'
                        ? t('cardTransfers.feeIncludedDescIncoming', 'Commission will be deducted from the amount. Customer receives less cash.')
                        : t('cardTransfers.feeIncludedDescOutgoing', 'Commission will be deducted from the amount. Card sends less money.'))
                    : (transferType === 'incoming'
                        ? t('cardTransfers.feeOnTopDescIncoming', 'Commission is separate. Customer receives full amount as cash.')
                        : t('cardTransfers.feeOnTopDescOutgoing', 'Commission is added on top. Customer pays amount + fee.'))
                  }
                </p>
              </label>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="input-field"
              placeholder={t('cardTransfers.notesPlaceholder')}
            />
          </div>

          {/* Summary */}
          {formData.amount && (
            <div className={`p-4 rounded-lg border-2 ${transferType === 'incoming'
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
              }`}>
              {/* Card Account Change */}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  {transferType === 'incoming'
                    ? t('cardTransfers.cardReceives', 'Card Receives')
                    : t('cardTransfers.cardSends', 'Card Sends')}:
                </span>
                <span className={`text-xl font-bold ${transferType === 'incoming' ? 'text-green-600' : 'text-red-600'}`}>
                  {transferType === 'incoming'
                    ? formatCurrency(parseFloat(formData.amount || 0), currencySymbol)
                    : (formData.feeIncluded
                        ? formatCurrency(parseFloat(formData.amount || 0) - parseFloat(formData.commission || 0), currencySymbol)
                        : formatCurrency(parseFloat(formData.amount || 0), currencySymbol))
                  }
                </span>
              </div>

              {/* Commission */}
              {parseFloat(formData.commission || 0) > 0 && (
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                  <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                    {t('cardTransfers.commission')} ({formData.feeIncluded ? t('cardTransfers.deducted', 'deducted') : t('cardTransfers.onTop', 'on top')}):
                  </span>
                  <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    +{formatCurrency(parseFloat(formData.commission || 0), currencySymbol)}
                  </span>
                </div>
              )}

              {/* Customer Cash */}
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                <span className="text-sm font-medium">
                  {transferType === 'incoming'
                    ? t('cardTransfers.customerReceivesCash', 'Customer Receives (Cash)')
                    : t('cardTransfers.customerPaysCash', 'Customer Pays (Cash)')}:
                </span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {transferType === 'incoming'
                    ? (formData.feeIncluded
                        ? formatCurrency(parseFloat(formData.amount || 0) - parseFloat(formData.commission || 0), currencySymbol)
                        : formatCurrency(parseFloat(formData.amount || 0), currencySymbol))
                    : (formData.feeIncluded
                        ? formatCurrency(parseFloat(formData.amount || 0), currencySymbol)
                        : formatCurrency(parseFloat(formData.amount || 0) + parseFloat(formData.commission || 0), currencySymbol))
                  }
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={submitting}
              className={transferType === 'incoming' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              <PlusIcon className="h-5 w-5 mr-2 rtl:mr-0 rtl:ml-2" />
              {t('common.confirm')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Balance Modal */}
      <Modal
        isOpen={showBalanceModal}
        onClose={() => setShowBalanceModal(false)}
        title={
          <div className="flex items-center gap-2">
            <PencilIcon className="h-5 w-5 text-blue-500" />
            {t('cardTransfers.editBalance')} - {selectedAccount?.provider}
          </div>
        }
      >
        <form onSubmit={handleBalanceSubmit} className="space-y-4">
          {/* Current Balance */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">{t('cardTransfers.currentBalance')}:</span>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(selectedAccount?.balance)}
              </span>
            </div>
          </div>

          {/* New Balance */}
          <Input
            label={t('cardTransfers.newBalance') + ' (' + t('common.iqd') + ') *'}
            type="number"
            min="0"
            step="250"
            value={balanceFormData.balance}
            onChange={(e) => setBalanceFormData(prev => ({ ...prev, balance: e.target.value }))}
            placeholder="0"
          />

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.notes')}
            </label>
            <textarea
              value={balanceFormData.notes}
              onChange={(e) => setBalanceFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="input-field"
              placeholder={t('cardTransfers.balanceAdjustmentReason')}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => setShowBalanceModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CardTransfersPage;
